import { FC, useEffect, useCallback } from 'react';

import { useBlocker, Location, NavigationType } from 'react-router-dom';

// Message can be a string or a function that receives (nextLocation, action) and returns string or boolean
// If function returns true, navigation is allowed; if string, show confirm dialog
type MessageFn = (location: Location, action: NavigationType) => string | boolean;

interface NavigationBlockerProps {
  when: boolean;
  message: string | MessageFn;
}

/**
 * A component that blocks navigation when a condition is met.
 * This replaces the Prompt component from react-router v5.
 * Requires a data router (createHashRouter/createBrowserRouter with RouterProvider).
 */
export const NavigationBlocker: FC<NavigationBlockerProps> = ({ when, message }) => {
  const blocker = useBlocker(
    useCallback(
      ({
        nextLocation,
        historyAction
      }: {
        currentLocation: Location;
        nextLocation: Location;
        historyAction: NavigationType;
      }) => {
        if (!when) return false;

        const confirmMessage =
          typeof message === 'function' ? message(nextLocation, historyAction) : message;

        // If function returns true, allow navigation (don't block)
        if (confirmMessage === true) {
          return false;
        }

        // If function returns false, block without confirm
        if (confirmMessage === false) {
          return true;
        }

        // If string, show confirm dialog
        if (typeof confirmMessage === 'string') {
          return !window.confirm(confirmMessage);
        }

        return false;
      },
      [when, message]
    )
  );

  // Reset the blocker if blocked (user already confirmed or declined in the callback)
  useEffect(() => {
    if (blocker.state === 'blocked') {
      blocker.reset();
    }
  }, [blocker]);

  // Handle browser back/forward and tab close
  useEffect(
    function BeforeUnload() {
      if (!when) return;

      const handleBeforeUnload = (e: BeforeUnloadEvent) => {
        const msg = typeof message === 'string' ? message : 'You have unsaved changes';
        e.preventDefault();
        e.returnValue = msg;
        return msg;
      };

      window.addEventListener('beforeunload', handleBeforeUnload);
      return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    },
    [when, message]
  );

  return null;
};
